import React, { useEffect, useState } from 'react';
import { inventoryAPI, storesAPI, spaceTypesAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart3, Building2, Package, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function InventoryPage() {
  const [month, setMonth] = useState('');
  const [inventory, setInventory] = useState([]);
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [storeFilter, setStoreFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    setMonth(currentMonth);
    loadData(currentMonth);
  }, []);

  const loadData = async (selectedMonth) => {
    if (!selectedMonth) return;
    setLoading(true);
    try {
      const [invRes, storesRes, productsRes] = await Promise.all([
        inventoryAPI.getSummary(selectedMonth),
        storesAPI.getAll(),
        spaceTypesAPI.getAll()
      ]);
      setInventory(invRes.data);
      setStores(storesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error('Error al cargar inventario');
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = (newMonth) => {
    setMonth(newMonth);
    loadData(newMonth);
  };

  const getOccupancyColor = (occupied, capacity) => {
    const percentage = (occupied / capacity) * 100;
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 70) return 'text-amber-600';
    return 'text-emerald-600';
  };

  // Get unique regions
  const regions = [...new Set(stores.map(s => s.region))].sort();

  // Group inventory by store with filters
  const groupedInventory = stores
    .filter(store => {
      // Region filter
      if (regionFilter !== 'all' && store.region !== regionFilter) return false;
      // Store filter
      if (storeFilter !== 'all' && store.id !== storeFilter) return false;
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        if (!store.name.toLowerCase().includes(search) && 
            !store.city.toLowerCase().includes(search) &&
            !store.store_code.toLowerCase().includes(search)) {
          return false;
        }
      }
      return true;
    })
    .map(store => {
      let storeInventory = inventory.filter(item => item.store_id === store.id);
      if (productFilter !== 'all') {
        storeInventory = storeInventory.filter(item => item.space_type_id === productFilter);
      }
      return { store, items: storeInventory };
    })
    .filter(group => group.items.length > 0);

  return (
    <div data-testid="inventory-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900 mb-2">Inventario</h1>
        <p className="text-lg text-slate-600">Disponibilidad de productos por farmacia</p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <Label htmlFor="month">Mes</Label>
              <Input
                id="month"
                type="month"
                value={month}
                onChange={(e) => handleMonthChange(e.target.value)}
                data-testid="month-filter"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="search">Buscar</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  id="search"
                  placeholder="Nombre, ciudad, código..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-filter"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="region">Región</Label>
              <Select value={regionFilter} onValueChange={setRegionFilter}>
                <SelectTrigger className="mt-1" data-testid="region-filter">
                  <SelectValue placeholder="Todas las regiones" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las regiones</SelectItem>
                  {regions.map(region => (
                    <SelectItem key={region} value={region}>{region}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="store">Farmacia</Label>
              <Select value={storeFilter} onValueChange={setStoreFilter}>
                <SelectTrigger className="mt-1" data-testid="store-filter">
                  <SelectValue placeholder="Todas las farmacias" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las farmacias</SelectItem>
                  {stores.map(store => (
                    <SelectItem key={store.id} value={store.id}>
                      {store.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="product">Producto</Label>
              <Select value={productFilter} onValueChange={setProductFilter}>
                <SelectTrigger className="mt-1" data-testid="product-filter">
                  <SelectValue placeholder="Todos los productos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los productos</SelectItem>
                  {products.map(product => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {groupedInventory.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No hay datos de inventario para los filtros seleccionados</p>
              </CardContent>
            </Card>
          ) : (
            groupedInventory.map(({ store, items }) => (
              <Card key={store.id} className="border-2 border-slate-200" data-testid={`store-inventory-${store.id}`}>
                <CardHeader className="bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-xl">{store.name}</CardTitle>
                        <CardDescription>
                          {store.city}, {store.region} • Código: {store.store_code}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {store.segment_tags.map(tag => (
                        <Badge key={tag} variant="outline">Segmento {tag}</Badge>
                      ))}
                      <Badge variant="outline">Tráfico: {store.traffic_score}</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    {items.map((item) => {
                      const product = products.find(p => p.id === item.space_type_id);
                      return (
                        <div key={`${item.store_id}-${item.space_type_id}`} className="flex items-center gap-4 p-4 border border-slate-200 rounded-lg hover:border-primary/50 transition-colors">
                          <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Package className="w-6 h-6 text-slate-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-slate-900">{item.space_type_name}</h4>
                              <Badge 
                                variant={product?.category === 'digital' ? 'default' : 'secondary'}
                                className="text-xs"
                              >
                                {product?.category === 'digital' ? '📱' : '🏪'}
                              </Badge>
                            </div>
                            <div className="grid grid-cols-5 gap-4 text-sm">
                              <div>
                                <p className="text-slate-500">Capacidad</p>
                                <p className="font-semibold text-slate-900">{item.capacity}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Reservado</p>
                                <p className="font-semibold text-slate-900">{item.reserved}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Holds</p>
                                <p className="font-semibold text-amber-600">{item.holds}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Disponible</p>
                                <p className={`font-semibold ${getOccupancyColor(item.reserved + item.holds, item.capacity)}`}>
                                  {item.available_quantity}
                                </p>
                              </div>
                              <div>
                                <p className="text-slate-500">Ocupación</p>
                                <p className="font-semibold text-slate-900">
                                  {Math.round(((item.reserved + item.holds) / item.capacity) * 100)}%
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}
